"use client";
import { useState, useEffect, useRef } from "react";
import moment from "moment";
import Navbar from "../../components/admin/common/Navbar";
import VisaType from "../../components/admin/HomePage/VisaType";
import DateSelector from "../../components/admin/HomePage/DateSelector";
import WelcomeMessage from "../../components/admin/HomePage/WelcomeMessage";
import StatisticsCards from "../../components/admin/HomePage/StatisticsCards";
import UniversityLetters from "../../components/admin/HomePage/UniversityLetters";
import { useNavigate } from "react-router-dom";
import SuccessRateOverview from "../../components/admin/HomePage/SuccessRate";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchDashboardData,
  setCountries,
} from "../../redux/features/adminDashboardSlice";

const HomePage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const initialFetchDone = useRef(false);

  // Get data from Redux
  const {
    dashboardData,
    isLoading,
    countries,
    // Get saved date filters from Redux
    fromMonth: savedFromMonth,
    fromYear: savedFromYear,
    toMonth: savedToMonth,
    toYear: savedToYear,
  } = useSelector((state) => state.dashboard);

  const globalCountries = useSelector(
    (state) => state.globalCountries.globalCountries
  );

  // Local filter state - initialize with null (no pre-selected dates)
  const [fromMonth, setFromMonth] = useState(savedFromMonth || null);
  const [fromYear, setFromYear] = useState(savedFromYear || null);
  const [toMonth, setToMonth] = useState(savedToMonth || null);
  const [toYear, setToYear] = useState(savedToYear || null);

  const currentMonth = moment().format("MMMM");
  const currentYear = moment().format("YYYY");

  // Update local state when Redux state changes (for when navigating back to this page)
  useEffect(() => {
    if (savedFromMonth) setFromMonth(savedFromMonth);
    if (savedFromYear) setFromYear(savedFromYear);
    if (savedToMonth) setToMonth(savedToMonth);
    if (savedToYear) setToYear(savedToYear);
  }, [savedFromMonth, savedFromYear, savedToMonth, savedToYear]);

  // Initial fetch
  useEffect(() => {
    if (!initialFetchDone.current) {
      dispatch(fetchDashboardData({ countries: globalCountries }));
      dispatch(setCountries(globalCountries));
      initialFetchDone.current = true;
    }
  }, [dispatch, globalCountries]);

  // Handle date and country changes
  useEffect(() => {
    if (!initialFetchDone.current) return;
    
    const timer = setTimeout(() => {
      dispatch(
        fetchDashboardData({
          fromMonth: fromMonth || null,
          fromYear: fromYear || null,
          toMonth: toMonth || null,
          toYear: toYear || null,
          countries: globalCountries,
        })
      );
      
      if (JSON.stringify(countries) !== JSON.stringify(globalCountries)) {
        dispatch(setCountries(globalCountries));
      }
    }, 300); // Small debounce to prevent rapid updates
    
    return () => clearTimeout(timer);
  }, [fromMonth, fromYear, toMonth, toYear, globalCountries, dispatch]);

  const redirectToLead = (status) => {
    const s = String(status || "").toLowerCase();
    // Finalized -> history
    if (s.includes("finalized")) {
      const stateData = {
        stateFromMonth: fromMonth,
        stateFromYear: fromYear,
        stateToMonth: toMonth,
        stateToYear: toYear,
        stateStatus: "Finalized",
      };
      return navigate("/admin/client-history", { state: stateData });
    }

    // Build state for Leads
    const stateData = {
      stateFromMonth: fromMonth,
      stateFromYear: fromYear,
      stateToMonth: toMonth,
      stateToYear: toYear,
    };

    // Leave "All Leads" unchanged: no preset status
    if (s.includes("all") && s.includes("lead")) {
      return navigate("/admin/leads", { state: stateData });
    }

    // Map to compact tokens consumed by Leads page
    if (s.includes("visa") && s.includes("applied")) {
      stateData.stateStatus = "Visa Applied"; // 
    } else if (s.includes("payment") && s.includes("pending")) {
      stateData.stateStatus = "Payment"; // MainLead maps Payment -> "Payment Pending"
    } else if (s.includes("transaction") || s.includes("approval")) {
      stateData.stateStatus = "Transaction Approval Pending"; // handled directly
    } else if (s.includes("active")) {
      stateData.stateStatus = "Active/In Progress"; // pass full label
    } else if (s.includes("pending")) {
      stateData.stateStatus = "Pending"; // handled directly
    }
    navigate("/admin/leads", { state: stateData });
  };

  const redirectToLeadWithCategory = (category) => {
    const stateData = {
      stateCategory: category,
      stateFromMonth: fromMonth,
      stateFromYear: fromYear,
      stateToMonth: toMonth,
      stateToYear: toYear,
    };

    navigate("/admin/leads", { state: stateData });
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="p-4 md:p-8">
        <WelcomeMessage dashboardData={dashboardData}/>
        <div className="flex flex-col md:flex-row gap-4 justify-start items-start md:items-center md:justify-between mt-6 mb-8">
          <h2 className="text-sm md:text-xl">{`${currentMonth} ${currentYear}`}</h2>
          <div className="max-w-full md:max-w-[375px] w-full flex items-center justify-center">
          <DateSelector
            className="flex md:flex-row flex-col gap-4 w-full"
            fromMonth={fromMonth}
            setFromMonth={setFromMonth}
            fromYear={fromYear}
            setFromYear={setFromYear}
            toMonth={toMonth}
            setToMonth={setToMonth}
            toYear={toYear}
            setToYear={setToYear}
          />
            </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="w-8 h-8 border-4 border-blue-900 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            <StatisticsCards
              handleClick={redirectToLead}
              stats={dashboardData?.stats}
            />
            <div className="grid grid-cols-1 gap-5 xl:flex xl:flex-row pt-5">
              <div className="xl:w-1/3">
                <VisaType
                  redirectToLeadWithCategory={redirectToLeadWithCategory}
                  stats={dashboardData?.stats}
                />
              </div>
              <div className="xl:w-2/3 flex flex-col gap-5">
                <UniversityLetters letters={dashboardData?.stats} />
                <SuccessRateOverview stats={dashboardData?.stats}/>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default HomePage;
